import { useCallback, useEffect, useRef, useState } from 'react';
import { draftChat } from '@/constants';
import { clearUnsentPrompt, selectUnsentPrompt, setUnsentPrompt, useAppDispatch, useAppSelector } from '@/store';

/**
 * Storage key for a chat box's unsent prompt: the chat id, or a per-scope draft key for a new chat. No scope = no key.
 */
export function getUnsentPromptKey(chatId: string | undefined, scope: string | undefined): string | undefined {
  if (scope === undefined) return undefined;
  return chatId ?? `${draftChat}:${scope}`;
}

/**
 * The chat box's unsent prompt, restored when the user returns. It's written to the store only on
 * unmount and removed as soon as a chat box shows it, so a new chat never inherits another's
 * prompt. A chat box that mounts before the old one stored its prompt must neither take nor
 * overwrite it.
 */
export function useUnsentPrompt({ chatId, scope }: { chatId: string | undefined; scope: string | undefined }) {
  const dispatch = useAppDispatch();
  const key = getUnsentPromptKey(chatId, scope);
  const unsentPrompt = useAppSelector((state) => selectUnsentPrompt(state, key)) ?? '';
  const [chatQuery, setChatQuery] = useState(unsentPrompt);

  // Latest key and prompt, used on unmount. A chat created meanwhile keeps its prompt under its own id.
  const latestRef = useRef({ key, chatQuery });
  useEffect(() => {
    latestRef.current = { key, chatQuery };
  }, [key, chatQuery]);

  useEffect(() => {
    // Only a prompt the chat box rendered with is its to take; see the hook's description.
    const shown = latestRef.current;
    if (shown.key && shown.chatQuery) {
      dispatch(clearUnsentPrompt({ key: shown.key }));
    }
    return () => {
      const left = latestRef.current;
      if (left.key) {
        dispatch(setUnsentPrompt({ key: left.key, prompt: left.chatQuery }));
      }
    };
  }, [dispatch]);

  /** For switching chats without unmounting: stores the old chat's prompt and returns the new chat's one. */
  const switchUnsentPrompt = useCallback(
    (leftChatId: string | undefined) => {
      const leftKey = getUnsentPromptKey(leftChatId, scope);
      if (leftKey) {
        dispatch(setUnsentPrompt({ key: leftKey, prompt: latestRef.current.chatQuery }));
      }
      if (key) {
        dispatch(clearUnsentPrompt({ key }));
      }
      return unsentPrompt;
    },
    [dispatch, key, scope, unsentPrompt],
  );

  /** For a chat box that sends and then unmounts before the prompt clears: don't store the sent prompt. */
  const discardUnsentPrompt = useCallback(() => {
    latestRef.current = { ...latestRef.current, chatQuery: '' };
  }, []);

  return { chatQuery, setChatQuery, switchUnsentPrompt, discardUnsentPrompt };
}
